import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Alert, Flex, Form, Radio, Space, InputNumber } from 'antd';
import styled from '@emotion/styled';

import DatePicker from 'components/DatePicker/DatePicker';
import { formatDateToSubmit } from 'utilities/datesAndTimes';
import { SETTLEMENTS_CADENCE_VALUES } from 'utilities/constants';

import StepContainer from '../StepContainer';
import {
  disabledPaymentDate,
  getPaymentPlanEndDate,
  isDate29thTo31st,
} from './offerHelpers';
import usePaymentPlanQuery from './usePaymentPlanQuery';

const PaymentPlanGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 3fr;
  grid-column-gap: var(--spacing);
  align-items: start;
`;

const { Item } = Form;

const PaymentPlanForm = ({
  initialCadence,
  initialStartDate,
  initialDuration,
  balance,
  expirationDate,
  maxDurationMonths,
  formName,
  durationLabel,
  durationRequired,
  showDurationField = (selectedCadence) =>
    selectedCadence !== SETTLEMENTS_CADENCE_VALUES.ONE_TIME,
  setPaymentDetails,
  setSchedule,
  onSubmit,
  renderEstimatedPayments,
  embedded = false,
  form: formProp,
}) => {
  const [form] = Form.useForm(formProp);
  const [cadence, setCadence] = useState(initialCadence);
  const [startDate, setStartDate] = useState(initialStartDate);
  const [duration, setDuration] = useState(initialDuration);
  const [dateResetMsg, setDateResetMsg] = useState('');

  const { retrievePaymentPlan, resetError, schedule, loading, error } =
    usePaymentPlanQuery();

  useEffect(() => {
    if (!loading && schedule.length) {
      setSchedule(schedule);
    }
  }, [loading, schedule]);

  const getDurationMonths = (selectedCadence, planDuration) => {
    if (planDuration) {
      return planDuration;
    }
    if (selectedCadence === SETTLEMENTS_CADENCE_VALUES.ONE_TIME) {
      return 1;
    }
    return maxDurationMonths;
  };

  const fetchPlan = (start, duration, cadence) => {
    resetError();
    if (start && cadence && duration) {
      setSchedule([]);
      retrievePaymentPlan({
        cadence,
        amount: balance,
        startDate: formatDateToSubmit(start),
        endDate: formatDateToSubmit(
          getPaymentPlanEndDate(start, duration, expirationDate),
        ),
      });
    }
  };

  const handleCadenceChange = ({ target: { value } }) => {
    setDateResetMsg('');
    setCadence(value);

    let currentStartDate = startDate;
    const durationMonths = getDurationMonths(value, duration);

    if (
      value === SETTLEMENTS_CADENCE_VALUES.MONTHLY &&
      form.getFieldValue('firstPaymentDate')?.date() >= 29
    ) {
      form.setFieldValue('firstPaymentDate', undefined);
      currentStartDate = null;
      setStartDate(null);
      setDateResetMsg(
        'Dates 29-31 are not valid for a MONTHLY cadence. Please select a new date.',
      );
    }

    setPaymentDetails({
      cadence: value,
      startDate: currentStartDate,
      duration: durationMonths,
    });
    fetchPlan(currentStartDate, durationMonths, value);
  };

  const handleDateChange = (selectedDate) => {
    setDateResetMsg('');
    const durationMonths = getDurationMonths(cadence, duration);

    setStartDate(selectedDate);
    setPaymentDetails({
      cadence,
      startDate: selectedDate,
      duration: durationMonths,
    });
    fetchPlan(selectedDate, durationMonths, cadence);
  };

  const handleDurationChange = (value) => {
    setDuration(value);
    const durationMonths = getDurationMonths(cadence, value);
    setPaymentDetails({ cadence, startDate, duration: value });
    fetchPlan(startDate, value ?? durationMonths, cadence);
  };

  const fields = (
    <PaymentPlanGrid>
      <div>
        <Form
          name={formName}
          form={form}
          layout="vertical"
          onFinish={onSubmit}
          initialValues={{
            planDuration: initialDuration,
            firstPaymentDate: initialStartDate
              ? dayjs(initialStartDate)
              : undefined,
            paymentCadence: initialCadence ?? null,
          }}
        >
          <Item
            label="Select a Payment Cadence"
            name="paymentCadence"
            rules={[
              {
                required: true,
                message: 'Please select a payment cadence.',
              },
            ]}
          >
            <Radio.Group onChange={handleCadenceChange}>
              <Space orientation="vertical">
                <Radio
                  data-testid="monthlyCadence"
                  value={SETTLEMENTS_CADENCE_VALUES.MONTHLY}
                >
                  Monthly
                </Radio>
                <Radio
                  data-testid="biweeklyCadence"
                  value={SETTLEMENTS_CADENCE_VALUES.BIWEEKLY}
                >
                  Every 2 Weeks
                </Radio>
                <Radio
                  data-testid="weeklyCadence"
                  value={SETTLEMENTS_CADENCE_VALUES.WEEKLY}
                >
                  Weekly
                </Radio>
                <Radio
                  data-testid="oneTimeCadence"
                  value={SETTLEMENTS_CADENCE_VALUES.ONE_TIME}
                >
                  Lump Sum
                </Radio>
              </Space>
            </Radio.Group>
          </Item>
          {showDurationField(cadence) && (
            <Item
              label={durationLabel}
              name="planDuration"
              rules={[
                ...(durationRequired
                  ? [
                      {
                        required: true,
                        message: 'Please enter a plan duration in months.',
                      },
                    ]
                  : []),
                {
                  validator: (_, value) => {
                    if (
                      value &&
                      maxDurationMonths &&
                      Number(value) > maxDurationMonths
                    ) {
                      return Promise.reject();
                    }
                    return Promise.resolve();
                  },
                  message: `Max duration is ${maxDurationMonths} months. Please re-enter.`,
                },
              ]}
            >
              <InputNumber
                data-testid="planDurationInput"
                onChange={handleDurationChange}
                min={1}
                style={{ width: 138, marginBottom: 'var(--spacing-sm)' }}
              />
            </Item>
          )}
          <Item
            label="Select a First Payment Date"
            name="firstPaymentDate"
            rules={[
              {
                required: true,
                message: 'Please select a first payment date.',
              },
            ]}
            style={{ marginBottom: '0px' }}
          >
            <DatePicker
              data-testid="paymentDatePicker"
              onChange={handleDateChange}
              disabledDate={(d) => {
                if (
                  expirationDate &&
                  d?.isSameOrAfter(dayjs(expirationDate), 'day')
                ) {
                  return true;
                }
                if (cadence === SETTLEMENTS_CADENCE_VALUES.MONTHLY) {
                  return disabledPaymentDate(d) || isDate29thTo31st(d);
                }
                return disabledPaymentDate(d);
              }}
              style={{ marginBottom: 'var(--spacing-sm)' }}
            />
          </Item>
          {dateResetMsg && (
            <Alert
              banner
              type="warning"
              title={dateResetMsg}
              style={{ width: '300px' }}
            />
          )}
          {error && (
            <Alert
              banner
              type="error"
              title="Failed to retrieve payment plan. Please try again."
              style={{ width: '250px' }}
            />
          )}
        </Form>
      </div>

      {renderEstimatedPayments(loading)}
    </PaymentPlanGrid>
  );

  if (embedded) {
    return fields;
  }

  return (
    <StepContainer
      title="Payment Plan Details"
      onContinue={() => form.submit()}
      buttonProps={{ disabled: loading || error }}
    >
      {fields}
    </StepContainer>
  );
};

export default PaymentPlanForm;
